// BROWSER-FIT СТАТИЧЕСКОГО ДОКУМЕНТА — один набор на все печатные дороги (тикет паспорта s969,
// `3f019875cbb881ef9bfbe5934a58ad90`).
//
// ⭐ ЗАЧЕМ. Паспорт печатается СТАТИКОЙ: `renderToStaticMarkup` в экспорте (`scripts/doc-html.mjs` →
// `export-doc.mjs`) и в документе нового окна шага 7 (`s7Document.js`, B9-6). У статики нет React-
// эффектов, поэтому всё, что по контракту «browser-fit» меряется в браузере, там не мерилось вовсе:
//   · §II — пагинация (s368.d2 / s410.d1) жила только в `useLayoutEffect` компонента → в статике
//     всегда заявленный план `[N]`, один лист на любое N (паспорт Бена, N=6: карточка I под подвалом);
//   · §IV — лестница уступок CD (`fitSheet`, канон F10) не была портирована вовсе → на M=6 карточка
//     VI под подвалом. CSS для неё (`rd-fit-cols` / `rd-fit-tight` / `--chart-h-redline`) был.
// Теперь пагинацию и подгонку меряют ОДНИ И ТЕ ЖЕ функции на всех дорогах — компонент, экспорт,
// новое окно, стенд гарда.
//
// ⚠️ `measureSectionII` и `fitDocument` СЕРИАЛИЗУЮТСЯ (`page.evaluate`, `<script>` в окне B9-6):
// внутри — ни одного внешнего имени, только аргумент. Добавляя код, не ссылайся на модуль.
//
// ⚠️ МАСШТАБ. В окне B9-6 стопка ужата `transform: scale(k)`, и `getBoundingClientRect` отдаёт
// ужатые пиксели, а константы раскладки (зазоры 22/10/7, уступки 388/300/+4) — в пикселях листа.
// Поэтому каждое измерение делится на k = высота рамки / `offsetHeight`. В экспорте k = 1.

/**
 * План листов §II по замеренным высотам — алгоритм s368.d2 + tail balance s410.d1, перенесён из
 * `SectionII.jsx` без изменений (там его история: F1 сирота, F2/F4 баланс, F3 guard, F4 hdContH).
 *
 * @param hs     высоты карточек в порядке печати (обратная хронология)
 * @param m      { avail, hdH, hdContH, subH, footH } — замер листа
 * @param dense  N ≥ 8 (плотная сетка, шаг карточек 7 вместо 10)
 * @returns {number[]|null} карточек на лист; null — раскладка не устоялась (шрифты), план не трогать
 */
export function planSheets(hs, m, dense) {
  const GAP = 22; // .st-sheet row gap, R9 A-1
  const CARD_GAP = dense ? 7 : 10;
  // F4: the first sheet's header and a continuation sheet's header are measured
  // separately (m.hdH / m.hdContH). On the one-line canon boards the two are equal so
  // the plan is unchanged; the split matters for the mirror doc, where the continuation
  // header wraps to a second line — a single hdH there overflowed the continuation sheet.
  const top = (first) => m.avail - (first ? m.hdH : m.hdContH) - GAP;
  const budget = (first, last) => top(first) - (first ? m.subH + GAP : 0) - (last ? GAP + m.footH : 0);
  // guard (F3): a budget smaller than the shortest card is an unsettled layout
  // (fonts not applied yet), NOT a reason to put one card per sheet. Return null and
  // keep the current plan; the pass repeats once layout settles. Without this the
  // degenerate [1,1,1,…] plan agrees with itself and the fixed-point loop declares
  // convergence on a wrong plan (latent since F1).
  if (!hs.length || Math.min(...hs) > budget(true, true)) return null;
  const out = [];
  let idx = 0;
  while (idx < hs.length) {
    const first = out.length === 0;
    let n = 0, acc = 0;
    while (idx + n < hs.length) {
      const add = hs[idx + n] + (n ? CARD_GAP : 0);
      if (acc + add > budget(first, idx + n + 1 === hs.length)) break;
      acc += add; n++;
    }
    if (n === 0) n = 1; // card taller than the sheet: one per sheet, never cut
    out.push(n); idx += n;
  }
  // orphan rule (F1)
  if (out.length > 1 && out[out.length - 1] === 1 && out[out.length - 2] > 2) {
    const i0 = hs.length - 2;
    const tail = hs[i0] + CARD_GAP + hs[i0 + 1];
    const prev = hs.slice(hs.length - 1 - out[out.length - 2], i0).reduce((a, b, i) => a + b + (i ? CARD_GAP : 0), 0);
    if (tail <= budget(false, true) && prev <= budget(out.length === 2, false)) {
      out[out.length - 2]--; out[out.length - 1] = 2;
    }
  }
  // TAIL BALANCE (F2, restored in F4 · s410.d1) — replaces the greedy tail. The greedy
  // pack above fixes the sheet COUNT S (minimal, unchanged); within S the cards spread
  // even — base = ⌈N/S⌉ on the leading sheets, the remainder on the last — so no tail
  // sheet stands two-thirds empty (greedy gave N=8 → 6+2, N=15 → 7+6+2; balanced gives
  // 4+4 / 5+5+5). Applied only if every sheet still fits its budget; otherwise the
  // greedy plan + orphan rule stands (honester than an overflow). The budget inside
  // fits() is the same measured budget(...) as the guard, so constants never come back.
  const fits = (p) => {
    let i = 0;
    return p.every((n, si) => {
      let acc = 0;
      for (let k = 0; k < n; k++) acc += hs[i + k] + (k ? CARD_GAP : 0);
      i += n;
      return acc <= budget(si === 0, si === p.length - 1);
    });
  };
  if (out.length > 1) {
    const S = out.length;
    const base = Math.floor(hs.length / S);
    const rem = hs.length % S;
    const even = Array.from({ length: S }, (_, i) => base + (i < rem ? 1 : 0));
    if (even.join() !== out.join() && fits(even)) return even;
  }
  return out;
}

/**
 * Замер листов §II под `root` (элемент или документ). СЕРИАЛИЗУЕТСЯ — без внешних имён.
 * @returns {{ m, hs, dense, plan }|null} null — §II нет или вёрстки ещё нет (jsdom, до раскладки)
 */
export function measureSectionII(root) {
  const sections = Array.from(root.querySelectorAll('section.stages-page'));
  if (!sections.length) return null;
  const doc = root.ownerDocument || root;
  const win = doc.defaultView;
  const k = sections[0].getBoundingClientRect().height / sections[0].offsetHeight || 1;
  const H = (el) => (el ? el.getBoundingClientRect().height / k : 0);
  const cs = win.getComputedStyle(sections[0].querySelector('.st-sheet'));
  const avail = H(sections[0]) - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  if (!(avail > 0)) return null;
  const m = {
    avail,
    hdH: H(sections[0].querySelector('.st-hd')),
    hdContH: H((sections[1] || sections[0]).querySelector('.st-hd')),
    subH: H(sections[0].querySelector('.st-sub')),
    footH: H(sections[sections.length - 1].querySelector('.st-foot-note')),
  };
  const hs = [];
  const plan = sections.map((s) => {
    const cards = Array.from(s.querySelectorAll('.st-card'));
    cards.forEach((c) => hs.push(H(c)));
    return cards.length;
  });
  return { m, hs, dense: sections[0].getAttribute('data-density') === 'dense', plan };
}

/**
 * Довести план §II до неподвижной точки на статическом документе: замер → план → перерисовка.
 * План меняет высоты заголовка продолжения и сноски, те меняют план — как эффект компонента.
 *
 * @param measure  async () => результат `measureSectionII` в целевом документе
 * @param apply    async (plan) => перерисовать документ с этим планом §II
 * @returns {number[]|null} итоговый план (null — §II нет / пересборка не понадобилась)
 */
export async function settleSectionII(measure, apply, maxPasses = 4) {
  let applied = null;
  for (let i = 0; i < maxPasses; i += 1) {
    const r = await measure();
    if (!r) return applied;
    const next = planSheets(r.hs, r.m, r.dense);
    if (!next || next.join() === r.plan.join()) return applied;
    applied = next;
    await apply(next);
  }
  return applied;
}

/**
 * Подгонка статического документа после раскладки и шрифтов. СЕРИАЛИЗУЕТСЯ — без внешних имён.
 *   1. §IV — `fitSheet` канона CD F10 (`docs/design/r10/R10 - §IV - canon F10.html`), 1:1: при ≥5
 *      карточках — две колонки (узкий режим имя 18 / цитата 13.5); затем чарт 388 → не ниже 300;
 *      затем плотный шаг карточек; остаток подписывается в `data-fit`. Константы — CD, не наши.
 *   2. §VII — лестница `cl-fit-tight → smaller → min` (перенесена из `export-doc.mjs` без изменений).
 *   3. §III/§IV — подписи этапов: при наложении соседей весь чарт переходит на римские.
 *   0. §II — только штамп перелива листа (пагинация задаётся при сборке, `settleSectionII`).
 * Перелив, оставшийся после уступок, ставится в `data-overflow-px` раздела (сигнал, блокирующий
 * экспорт у CD, и предмет гарда `passport-docfit.spec.js`).
 */
export function fitDocument(root) {
  const scaleOf = (a) => a.getBoundingClientRect().height / a.offsetHeight || 1;

  // 0. §II — перелив листа после пагинации (её здесь нет: план задан при сборке, `settleSectionII`).
  // Низ последнего блока листа против верха подвала; > 0 → `data-overflow-px` на листе.
  root.querySelectorAll('section.stages-page').forEach((s) => {
    const sheet = s.querySelector('.st-sheet'), foot = s.querySelector('.ab-foot');
    if (!sheet || !foot) return;
    const k = scaleOf(s);
    const bottom = Math.max(...Array.from(sheet.children).map((b) => b.getBoundingClientRect().bottom));
    const o = Math.round((bottom - foot.getBoundingClientRect().top) / k);
    if (o > 0) s.setAttribute('data-overflow-px', String(o)); else s.removeAttribute('data-overflow-px');
  });

  // 1. §IV — CD fitSheet (F10)
  const CHART_H_BASE = 388, CHART_H_MIN = 300;
  root.querySelectorAll('.artboard').forEach((a) => {
    const cross = a.querySelector('.rd-cross'), foot = a.querySelector('.ab-foot');
    if (!cross || !foot) return;
    a.classList.remove('rd-fit-tight', 'rd-fit-cols');
    a.style.removeProperty('--chart-h-redline');
    delete a.dataset.fit;
    a.removeAttribute('data-overflow-px');
    const k = scaleOf(a);
    const over = () => Math.round((cross.getBoundingClientRect().bottom - foot.getBoundingClientRect().top) / k);
    let o = over(); if (o <= 0) return;
    const notes = [];
    if (a.querySelectorAll('.r4-ev').length >= 5) {
      a.classList.add('rd-fit-cols'); notes.push('свидетельства в две колонки'); o = over();
      if (o <= 0) { a.dataset.fit = notes.join(' · '); return; }
    }
    const cut = Math.min(o + 4, CHART_H_BASE - CHART_H_MIN), h = CHART_H_BASE - cut;
    a.style.setProperty('--chart-h-redline', h + 'px');
    notes.push(`чарт ${CHART_H_BASE} → ${h}px`);
    o = over();
    if (o > 0) { a.classList.add('rd-fit-tight'); notes.push('плотный шаг карточек'); o = over(); }
    if (o > 0) { notes.push(`остаток ${o}px`); a.setAttribute('data-overflow-px', String(o)); }
    a.dataset.fit = notes.join(' · ');
  });

  // 2. §VII fit ladder (canon F2 · s417 · cl-fit-tight → smaller → min, capacity 172px). The overflow
  // symptom is the course foot colliding with the page footer; a column flex-height quirk makes
  // .cl-sheet scrollHeight unreliable across environments, so measure the collision directly:
  // over = .cl-foot.bottom − .ab-foot.top. Add steps cumulatively while it collides. Any leftover
  // after the third step is stamped in data-overflow-px (CD's export-blocking signal).
  root.querySelectorAll('.closing-page').forEach((sec) => {
    const foot = sec.querySelector('.cl-foot');
    const page = sec.querySelector('.ab-foot');
    if (!foot || !page) return;
    const k = scaleOf(sec);
    const over = () => Math.round((foot.getBoundingClientRect().bottom - page.getBoundingClientRect().top) / k);
    for (const cls of ['cl-fit-tight', 'cl-fit-smaller', 'cl-fit-min']) {
      if (over() <= 0) break;
      sec.classList.add(cls);
    }
    sec.setAttribute('data-overflow-px', over() > 0 ? String(over()) : '');
  });

  // 3. Label-fit (§III/§IV `labelMode`, browser-fit) — `fitStageLabels` канона ОДИН В ОДИН
  // (`R10 - §III - canon F3.html:85`, `R10 - §IV - canon F10.html:356`): подпись «III · имя» шире своей
  // колонки минус 10 (ширина — `getBBox` в единицах графика, не меньше «знаков × 5.2») → ВСЕ подписи
  // графика римскими. До cc-213 здесь стояло своё правило — «соседи сошлись ближе 6 px», мягче канона:
  // на вводе 04 подпись III вылезала из колонки и почти касалась IV, но 6 px ещё «влезали» (проход CD
  // 12.10, тикет `3f819875cbb8817d9165ef46531db9df`).
  const PAD = 10;
  const CH = 5.2; // замеренная CD средняя ширина знака Newsreader italic 13.5px в единицах графика
  root.querySelectorAll('.lp-chart, .rd-chart').forEach((chart) => {
    const labels = Array.from(chart.querySelectorAll('.lp-chart-stagename, .rd-chart-stagename'));
    if (!labels.length) return;
    labels.forEach((t) => { const full = t.getAttribute('data-full'); if (full) t.textContent = full; });
    const over = labels.some((t) => {
      const full = t.getAttribute('data-full') || '';
      const room = Number(t.getAttribute('data-col-w')) - PAD;
      let w;
      try { w = t.getBBox().width; } catch { w = 0; } // нет замера (jsdom, скрытый узел) — только «знаки × 5.2»
      return Math.max(w, full.length * CH) > room;
    });
    labels.forEach((t) => {
      // Этап без имени (`data-placeholder`) всегда римский — у него полной подписи нет.
      if (t.getAttribute('data-placeholder')) return;
      const roman = t.getAttribute('data-roman');
      const full = t.getAttribute('data-full');
      t.setAttribute('data-label-mode', over ? 'roman' : 'full');
      if (over && roman) t.textContent = roman;
      else if (full) t.textContent = full;
    });
  });
}
